<template>
  <transition-group
    data-qa="spilka-formio"
    f-version="mf-1"
    name="s-zoom-in-center"
    tag="div">
    <div
      v-if="showLoader && !errorLoader"
      key="load-formio"
      class="block-formio__loader text-center">
      <sIcon
        class="my-auto mr-2"
        size="24"
        spin
        type="loading"/>
      <div
        class="my-auto"
        data-qa="formio-loading">
        {{ $t('Common.Loading') }}
      </div>
    </div>
    <sAlert
      v-else-if="hasError || errorLoader"
      key="alert"
      class="mt-4"
      data-qa="formio-error"
      show-icon
      type="error">
      {{ $t('Common.ErrorTitle') }}
      <template slot="desc">
        <div v-dompurify-html="errorLoader ||formData.errorMessage || errorMessage"/>
      </template>
    </sAlert>
    <div
      v-if="isFormReady"
      key="formio"
      class="block-formio"
      :class="{ 'block-formio--hidden': !formRendered }">
      <iframe
        ref="formioFrame"
        class="formio-frame"
        data-qa="formio-frame"
        frameborder="0"
        :src="iframeSrc"
        :style="{ height: frameHeight }"
        title="Formio"
        @load="onIframeLoad"/>
      <sButton
        v-if="showSubmitButton"
        data-qa="formio-submit"
        :disabled="error"
        @click="submit">
        {{ $t('Catalog.Send') }}
      </sButton>
      <slot/>
    </div>
    <div
      v-if="submissionData"
      key="formValues"
      class="formio-values"
      data-qa="formio-values">
      <template
        v-for="formValue in Object.entries(submissionData)">
        <div
          :key="formValue[0]"
          :data-qa="formValue[0]">
          {{ formValue[1] }}
        </div>
      </template>
    </div>
  </transition-group>
</template>
<script>
/**
 * Компонент для работы с formio (микрофронтенд).
 */

import formio from 'mixins/formio'
import { storageMixin } from 'mixins'
import theme from 'mixins/theme'
import { debounce } from 'toolkit/utils/util'
import textFunctions from 'mixins/textFunctions'

// Типы сообщений моста (см. @microfrontend/integration-contracts → FORMIO_EVENTS)
const MSG = {
  // host → iframe
  RENDER: 'formio:render',
  VALIDATE: 'formio:validate',
  SUBMIT: 'formio:submit',
  GET_SUBMISSION: 'formio:get-submission',
  SET_LANGUAGE: 'formio:set-language',
  RESET: 'formio:reset',
  THEME: 'formio:theme',
  PARENT_CLICK: 'formio:parent-click',
  // iframe → host
  READY: 'formio:ready',
  INITIALIZED: 'formio:initialized',
  CHANGE: 'formio:change',
  CUSTOM_EVENT: 'formio:custom-event',
  SUBMIT_VALID: 'formio:submit-valid',
  SUBMIT_ERROR: 'formio:submit-error',
  VALIDATE_RESULT: 'formio:validate-result',
  SUBMISSION: 'formio:submission',
  HEIGHT: 'formio:height',
  ERROR: 'formio:error',
  CLICK: 'formio:click',
}

const DARK_THEME = 'dark-theme'

// События микрофронтенда, после которых форма считается отрисованной
const FORM_RENDER_EVENTS = ['formLoad', 'render']

export default {
  name: 'spilkaFormioMF',
  mixins: [storageMixin, theme, formio, textFunctions],
  data() {
    return {
      keyForm: 1,
      errorMessage: '',
      errorLoader: '',
      language: this.$i18n.locale,
      formData: null,
      iframeReady: false,
      formRendered: false,
      frameHeight: '300px',
      reqSeq: 0,
      pendingRequests: {},
    }
  },
  methods: {
    onIframeLoad() {
      this.errorLoader = ''
      if (!this.iframeReady) {
        this.markFrameReady()
      }
      try {
        const iframe = this.$refs?.formioFrame
        let iframeDoc = null
        try {
          iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document
        } catch (e) {
          if (e.name !== 'SecurityError') {
            throw e
          } else {
            return
          }
        }

        if (!iframeDoc || iframeDoc.body.innerHTML === '') {
          this.errorLoader = 'Схоже, фрейм не зміг завантажитись'
        }
      } catch (e) {
        if (e.name !== 'SecurityError') {
          this.errorLoader = e?.message || String(e)
        }
      }
    },
    markFrameReady() {
      this.iframeReady = true
      this.pushFormToFrame()
      this.sendTheme()
    },
    nextRequestId() {
      this.reqSeq += 1
      return `req_${this.reqSeq}`
    },
    postToFrame(type, payload = {}) {
      const frameWindow = this.$refs.formioFrame?.contentWindow
      if (!frameWindow) { return }
      frameWindow.postMessage({ type, ...payload }, this.iframeOrigin)
    },
    sendTheme() {
      this.postToFrame(MSG.THEME, { theme: this.userTheme === DARK_THEME ? 'dark' : 'light' })
    },
    pushFormToFrame() {
      if (!this.iframeReady || !this.formData || !this.formData.success) { return }
      this.postToFrame(MSG.RENDER, {
        form: this.toPlain(this.formData),
        options: this.toPlain(this.frameOptions),
        language: this.language,
        formKey: this.keyForm,
      })
    },
    /** Снять реактивные обёртки/заморозку перед postMessage (структурное клонирование) */
    toPlain(value) {
      try {
        return JSON.parse(JSON.stringify(value))
      } catch (_) {
        return value
      }
    },
    onFrameMessage(event) {
      if (!this.$refs.formioFrame || event.source !== this.$refs.formioFrame.contentWindow) {
        return
      }
      const data = event.data
      if (!data || !data.type) { return }

      switch (data.type) {
      case MSG.READY:
        this.markFrameReady()
        break
      case MSG.INITIALIZED:
        this.$emit('initialized', data.value)
        break
      case MSG.CHANGE:
        this.$set(this.formData, 'submission', { data: data.data })
        break
      case MSG.CUSTOM_EVENT:
        this.handleCustomEvent(data.event)
        break
      case MSG.SUBMIT_VALID:
        this.$emit('submit-valid', data.result)
        break
      case MSG.SUBMIT_ERROR:
        this.$emit('submit-error', data.error)
        break
      case MSG.VALIDATE_RESULT:
        this.resolvePending(data.requestId, { result: data.result, errors: data.errors || [] })
        break
      case MSG.SUBMISSION:
        this.resolvePending(data.requestId, data.data)
        break
      case MSG.HEIGHT:
        if (data.height) { this.frameHeight = `${data.height}px` }
        break
      case MSG.ERROR:
        this.$emit('error', data.message)
        break
      case MSG.CLICK:
        this.$emit('iframe-click')
        break
      default:
        if (typeof data.type === 'string' && data.type.startsWith('formio:') && data.event) {
          if (FORM_RENDER_EVENTS.includes(data.event)) {
            this.formRendered = true
          }
          this.$emit(data.event, data.value)
        }
      }
    },
    resolvePending(requestId, payload) {
      const resolver = this.pendingRequests[requestId]
      if (resolver) {
        delete this.pendingRequests[requestId]
        resolver(payload)
      }
    },
    handleCustomEvent(eventVal) {
      this.$emit('customEvent', eventVal)
      this.$spinner.show()
      const eventModel = {
        formCode: this.formParams?.formCode,
        tenderId: this.formParams?.tenderId,
        diagramId: null,
        eventCode: eventVal?.type,
        submission: JSON.stringify(this.formData.submission?.data ?? {}),
        components: JSON.stringify(this.formData.components),
      }
      this.$_callCustomEvent(eventModel)
        .then((result) => {
          if (result.success) {
            this.errorMessage = ''
            this.$_setFormProps(result)
              .then((formData) => {
                this.$set(this, 'formData', formData)
                this.refreshKeyForm()
              })
          } else {
            this.errorMessage = result.errorMessage || result.errorCode
            this.$notice.warning({
              title: this.$t('Common.ErrorTitle'),
              message: this.errorMessage,
              duration: 10,
            })
          }
        })
        .finally(() => {
          this.$spinner.close()
        })
    },
    async saveDraft() {
      if (!this.keyDraft) {
        this.$sinfo('Немає ключа чернетки.')
        return
      }
      const submission = this.$_removeKey(
        {
          ...this.formData?.submission?.data ?? {},
        },
        'Files',
      )
      if (!submission) {
        return
      }
      await this.$_savePropToDBStorage(this.keyDraft, submission)
    },
    async applyDraft() {
      if (!this.keyDraft) {
        this.$sinfo('Немає ключа чернетки.')
        return
      }
      return this.$_getPropFromDBStorage(this.keyDraft, null, false)
        .then((draftValue) => {
          if (!draftValue) { return null }
          if (this.formData) {
            this.formData.submission = {
              data: draftValue,
            }
            this.refreshKeyForm()
          }
          return draftValue
        })
    },
    refreshKeyForm() {
      this.keyForm += 1
      this.pushFormToFrame()
    },
    /**
     * Проверить валидность формы. Возвращает Promise<boolean>.
     */
    checkValidity() {
      return this.validate().then(result => result.result)
    },
    validate() {
      return new Promise((resolve) => {
        if (!this.iframeReady) {
          const res = { result: false, errors: [this.$t('Errors.STHWrong')] }
          this.$emit('validate', res)
          resolve(res)
          return
        }
        const requestId = this.nextRequestId()
        const timeout = setTimeout(() => {
          delete this.pendingRequests[requestId]
          const res = { result: false, errors: [this.$t('Errors.STHWrong')] }
          this.$emit('validate', res)
          resolve(res)
        }, 8000)
        this.pendingRequests[requestId] = (payload) => {
          clearTimeout(timeout)
          const res = { result: !!payload.result, errors: payload.errors || [] }
          this.$emit('validate', res)
          resolve(res)
        }
        this.postToFrame(MSG.VALIDATE, { requestId })
      })
    },
    /**
     * Применить форму (программный submit). Результат придёт событиями
     * @submit-valid / @submit-error.
     */
    submit: debounce(function () {
      this.postToFrame(MSG.SUBMIT)
    }, 250),
    /**
     * Получить актуальные данные submission из iframe.
     */
    getSubmission() {
      return new Promise((resolve) => {
        if (!this.iframeReady) {
          resolve(this.formData?.submission?.data ?? {})
          return
        }
        const requestId = this.nextRequestId()
        const timeout = setTimeout(() => {
          delete this.pendingRequests[requestId]
          resolve(this.formData?.submission?.data ?? {})
        }, 5000)
        this.pendingRequests[requestId] = (payload) => {
          clearTimeout(timeout)
          resolve(payload)
        }
        this.postToFrame(MSG.GET_SUBMISSION, { requestId })
      })
    },
    resetForm() {
      this.postToFrame(MSG.RESET)
    },
    onDocumentClick(event) {
      const frame = this.$refs.formioFrame
      if (frame && frame.contains(event.target)) {
        return
      }
      this.postToFrame(MSG.PARENT_CLICK)
    },
    async loadData() {
      if (!this.formParams) {
        this.$notice.error({
          title: this.$t('Common.ErrorTitle'),
          dangerouslyUseHTMLString: true,
          message: this.$t('Common.IncorrectParametersEmptyValues'),
          showClose: true,
          progress: true,
        })
        this.formData = {}
        return
      }
      if (!this.formParams.formCode) {
        this.$notice.error({
          title: this.$t('Common.ErrorTitle'),
          dangerouslyUseHTMLString: true,
          message: this.$t('Common.IncorrectParametersCodeMandatory'),
          showClose: true,
          progress: true,
        })
        this.formData = {}
        return
      }
      const formParams = {
        formCode: this.formParams.formCode,
        contactId: this.formParams.contactId,
        tenderId: this.formParams.tenderId ?? this.formParams.params?.find(i => i.key === 'tenderId')?.value,
      }
      await this.$_getNewForm(formParams)
        .then(async (formData) => {
          this.applyDraft()
            .then((draftValue) => {
              if (draftValue) {
                formData.submission = {
                  data: draftValue,
                }
              }
            })
            .finally(() => {
              this.setDisabledComponents(formData)
              this.$set(this, 'formData', formData)
            })
        })
        .finally(() => {
          this.refreshKeyForm()
        })
    },
    /**
     * Рекурсивно проставить disabled = true всем компонентам,
     * включая вложенные (container, columns, datagrid, table, panel и т.д.)
     */
    setDisabledComponents(formData) {
      if (!this.disabled || !Array.isArray(formData?.components)) {
        return
      }
      formData.components.forEach((component) => {
        if (!component) {
          return
        }
        component.disabled = true
        this.setDisabledComponents(component)

        if (Array.isArray(component.columns)) {
          component.columns.forEach(column => this.setDisabledComponents(column))
        }

        if (Array.isArray(component.rows)) {
          component.rows.forEach((row) => {
            if (Array.isArray(row)) {
              row.forEach(cell => this.setDisabledComponents(cell))
            }
          })
        }
      })
    },
  },
  computed: {
    submissionData() {
      return this.formData?.submission?.data
    },
    error() {
      return this.showAlert && !!this.errorMessage?.length
    },
    isFormReady() {
      return !!(this.formData && this.formData.success && !this.error)
    },
    hasError() {
      return !!(this.formData && (!this.formData.success || this.error))
    },
    showLoader() {
      return !this.formData || (this.isFormReady && !this.formRendered)
    },
    iframeSrc() {
      const microserviceUrl = (window.config.MicroserviceUrl || '/').replace(/\/$/, '')
      return `${microserviceUrl}/app/mf/formio-builder/?lang=${this.language}`
      //return `http://localhost:3004/app/mf/formio-builder/?lang=${this.language}`
    },
    iframeOrigin() {
      try { return new URL(this.iframeSrc, window.location.href).origin } catch { return '*' }
    },
    frameOptions() {
      return this.options || {}
    },
    showSubmitButton() {
      if (!this.formData?.components?.length) { return false }

      return !this.hideSubmit && !this.existsSubmitButton
    },
    existsSubmitButton() {
      return !!this.form?.components?.find(i => i.type === 'button' && i.action === 'submit')
    },
  },
  async mounted() {
    window.addEventListener('message', this.onFrameMessage)
    document.addEventListener('click', this.onDocumentClick, true)
    if (!this.form && this.formParams) {
      await this.loadData()
    }
  },
  beforeDestroy() {
    window.removeEventListener('message', this.onFrameMessage)
    document.removeEventListener('click', this.onDocumentClick, true)
  },
  watch: {
    form: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (!newValue) { return }
        const formData = { ...newValue }
        this.setDisabledComponents(formData)
        this.$set(this, 'formData', formData)
        this.refreshKeyForm()
      },
    },
    '$i18n.locale': function (newLocale) {
      this.language = newLocale
      this.postToFrame(MSG.SET_LANGUAGE, { language: newLocale })
    },
    userTheme(newValue, oldValue) {
      if (newValue !== oldValue && this.iframeReady) {
        this.sendTheme()
      }
    },
  },
  props: {
    /**
     * JSON directly
     */
    form: {
      type: Object,
    },
    /**
     * ключ для черновика. Если не передать, функционал черновика работать не будет
     */
    keyDraft: {
      type: String,
    },
    /**
     * Параметры формы из RMD
     * {
     * formCode: String // код формы
     * params: Array // массив параметров {key, value}
     * }
     */
    formParams: {
      type: Object,
    },
    /**
    * {
    *   readOnly: false,
    * },
    */
    options: {
      type: Object,
    },
    /**
     * Показывать блок со списком ошибок
     */
    showAlert: {
      type: Boolean,
      default: true,
    },
    hideSubmit: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
}
</script>

<style lang="scss" scoped>
.block-formio {
  width: 100%;

  &--hidden {
    display: none;
  }

  &__loader {
    /*display: flex;
    align-items: center;
    justify-content: center;*/
    min-height: 64px;
  }
}
.formio-frame {
  width: 100%;
  min-height: 64px;
  border: 0;
  display: block;
}
.formio-values {
  display: none;
}
</style>
